import { Label } from "@/atoms-react/text/Text.react";
import { InformationCircleIcon } from "@heroicons/react/24/solid";
import { useTranslation } from "react-i18next";
import { bronzeFormStyle } from "../bronze-form-style";
import { bronzeFormTranslation, isHttpUrlValid } from "../utils";
import InputLabel from "@/atoms-react/input/Input.react";
import { memo, useState } from "react";
import { twMerge } from "tailwind-merge";
import toast from "react-hot-toast";

type BronzeFormInputTextType = "URL" | "Text";

type BronzeFormInputTextProps = {
  type: BronzeFormInputTextType;
  logicalState: string;
  logicalStateSetter: (document: string) => void;
  translationKey: string;
  rows?: number;
  placeholder?: string;
  errorMessage?: string;
  maxLength?: number;
};

export const BronzeFormInputText = memo(
  (props: BronzeFormInputTextProps) => {
    const {
      type,
      logicalState,
      logicalStateSetter,
      translationKey,
      rows,
      placeholder,
      maxLength = 255
    } = props;
    const t = useTranslation(["pass"]);
    const [errorMessage, setErrorMessage] = useState<string | undefined>(props.errorMessage);

    const onBlur = () => {
      switch (type) {
        case "URL":
          if (!isHttpUrlValid(logicalState)) {
            setErrorMessage(bronzeFormTranslation(t, "input_text.error.invalid_url_error"));
          } else setErrorMessage(undefined);
          break;
        default:
      }
    };

    const onChange = (newValue: string) => {
      if (logicalState.length + 1 <= maxLength || newValue.length < logicalState.length) {
        logicalStateSetter(newValue);
      } else {
        toast.error(
          `${bronzeFormTranslation(t, "input_text.error.too_large")} ${
            logicalState.length
          }/${maxLength}`,
          { id: `input-text-${translationKey}`, duration: 1000 }
        );
      }
    };

    return (
      <div className={bronzeFormStyle.containerLine}>
        <div className={bronzeFormStyle.containerLineTitle}>
          <Label className={bronzeFormStyle.containerLineTitleText}>
            {bronzeFormTranslation(t, `${translationKey}.title`)}
          </Label>
          <div className={bronzeFormStyle.tooltipContainer}>
            <InformationCircleIcon
              className={bronzeFormStyle.tooltipContainerIcon}
              data-tooltip={bronzeFormTranslation(t, `${translationKey}.tooltip`)}
              data-position="right"
            />
          </div>
        </div>
        <div className={bronzeFormStyle.containerLineContent}>
          <InputLabel
            dataCy={`bronze.form.${translationKey}`}
            value={logicalState}
            placeholder={placeholder}
            onChange={onChange}
            onBlur={onBlur}
            className={twMerge("mb-1 flex-1 md:mb-0", errorMessage && "border-red-400")}
            rows={rows ?? 1}
          />
          {/* Show Text Error */}
          {errorMessage && <Label className="text-red-400">{errorMessage}</Label>}
        </div>
      </div>
    );
  },
  (prevProps: BronzeFormInputTextProps, nextProps: BronzeFormInputTextProps): boolean => {
    return (
      prevProps.type === nextProps.type &&
      prevProps.logicalState === nextProps.logicalState &&
      prevProps.translationKey === nextProps.translationKey &&
      prevProps.placeholder === nextProps.placeholder &&
      prevProps.rows === nextProps.rows &&
      prevProps.errorMessage === nextProps.errorMessage &&
      prevProps.maxLength === nextProps.maxLength
    );
  }
);
